import { AlertTriangle, Image as ImageIcon, Play } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { sourcePlatformLabel, type AdSummary } from "../../shared/types";
import { singleTagHref } from "../libraryTags";
import { CopyablePublicId } from "./CopyablePublicId";

function duration(value: number | null): string {
  if (value === null) return "";
  const minutes = Math.floor(value / 60);
  const seconds = Math.round(value % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function relativeTime(value: string): string {
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function statusLabel(ad: AdSummary): string {
  if (ad.ingestionState === "review_required") return "Needs review";
  if (ad.ingestionState === "ready") return "Ready";
  return ad.ingestionState.replaceAll("_", " ");
}

export function AdCard({ ad }: { ad: AdSummary }) {
  const video = useRef<HTMLVideoElement>(null);
  const play = () => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const element = video.current;
    if (element?.paused) void element.play().catch(() => undefined);
  };
  const stop = () => {
    const element = video.current;
    if (!element) return;
    element.pause();
    element.currentTime = 0;
  };
  const title = ad.scriptTitle || ad.product || ad.filename;
  const isImage = ad.creativeMedium === "image";
  return <article className="ad-card" onMouseEnter={isImage ? undefined : play} onPointerEnter={isImage ? undefined : (event) => { if (event.pointerType !== "touch") play(); }} onMouseLeave={isImage ? undefined : stop} onPointerLeave={isImage ? undefined : stop}>
    <Link className="ad-card__media-link" to={`/ads/${encodeURIComponent(ad.id)}`} aria-label={`Open ${title}`}>
      <div className="ad-card__media">
        {ad.mediaUrl ? isImage ? <img src={ad.posterUrl ?? ad.mediaUrl} alt={`Preview ${title}`} loading="lazy" /> : <video ref={video} src={ad.mediaUrl} poster={ad.posterUrl ?? undefined} muted playsInline preload="metadata" aria-label={`Preview ${title}`} /> : <div className="ad-card__placeholder">SD</div>}
        {!isImage && <span className="ad-card__play" aria-hidden="true"><Play size={21} fill="currentColor" /></span>}
        {!isImage && ad.durationSeconds !== null && <span className="ad-card__duration">{duration(ad.durationSeconds)}</span>}
        {isImage && <span className="ad-card__medium"><ImageIcon size={13} />Image</span>}
      </div>
    </Link>
    <div className="ad-card__body">
      {ad.ingestionState === "review_required" && <div className="card-review-alert"><AlertTriangle size={14} />Blocked on human review</div>}
      {ad.maintenanceIssueCount > 0 && <Link className="card-review-alert" to="/operations"><AlertTriangle size={14} />Ready with {ad.maintenanceIssueCount} maintenance issue{ad.maintenanceIssueCount === 1 ? "" : "s"}</Link>}
      <Link className="ad-card__title-link" to={`/ads/${encodeURIComponent(ad.id)}`}><h2>{title}</h2></Link>
      <p>{ad.brand || "Unknown brand"}</p>
      <div className="ad-card__tags" aria-label="Ad tags">{ad.tags.map((tag) => <Link className="product-tag" key={tag.slug} to={singleTagHref(tag.slug)}>{tag.label}</Link>)}</div>
      <CopyablePublicId value={ad.publicId} label="Ad ID" className="ad-card__id" />
    </div>
    <footer className="ad-card__footer">
      <span className={`card-state card-state--${ad.ingestionState}`}><i />{statusLabel(ad)}</span>
      <span>{sourcePlatformLabel(ad.sourcePlatform)}</span><time dateTime={ad.updatedAt}>{relativeTime(ad.updatedAt)}</time>
    </footer>
  </article>;
}
